'use client';

import React, { useState, useMemo } from 'react';
import { PastryBagIcon, PlusCircleIcon, Trash2Icon, ClipboardIcon, CheckCircleIcon } from '../../Icons';

type CakeShape = 'round' | 'square';
type FrostingStyle = 'naked' | 'standard' | 'generous' | 'extra';

interface Tier {
    id: number;
    shape: CakeShape;
    size: string;
    layers: string;
}

const FROSTING_MULTIPLIERS: Record<FrostingStyle, number> = {
    naked: 0.4,
    standard: 1.0,
    generous: 1.5,
    extra: 2.0,
};

const LAYER_HEIGHT_INCHES = 2;
const FILLING_HEIGHT_INCHES = 0.25;
const CUPS_PER_POUND = 2;
const GRAMS_PER_POUND = 453.592;
const CUPS_TO_CUBIC_INCHES = 14.4375;

export const ButtercreamCalculatorClient: React.FC = () => {
    const [tiers, setTiers] = useState<Tier[]>([{ id: 1, shape: 'round', size: '8', layers: '3' }]);
    const [frostingStyle, setFrostingStyle] = useState<FrostingStyle>('standard');
    const [copied, setCopied] = useState<string | null>(null);

    const { totalCups, fillingCups, coatingCups } = useMemo(() => {
        let fillingVolume = 0;
        let coatingVolume = 0;

        tiers.forEach(tier => {
            const size = parseFloat(tier.size) || 0;
            const layers = parseFloat(tier.layers) || 0;
            const { shape } = tier;
            if (size <= 0 || layers <= 1) return;

            const radius = size / 2;
            const area = shape === 'round' ? Math.PI * radius * radius : size * size;
            const circumference = shape === 'round' ? 2 * Math.PI * radius : 4 * size;
            const cakeHeight = layers * LAYER_HEIGHT_INCHES;

            fillingVolume += area * FILLING_HEIGHT_INCHES * (layers - 1);
            coatingVolume += (area * 0.25) + (circumference * cakeHeight * 0.25);
        });

        const adjustedCoatingVolume = coatingVolume * FROSTING_MULTIPLIERS[frostingStyle];
        return {
            totalCups: (fillingVolume + adjustedCoatingVolume) / CUPS_TO_CUBIC_INCHES,
            fillingCups: fillingVolume / CUPS_TO_CUBIC_INCHES,
            coatingCups: adjustedCoatingVolume / CUPS_TO_CUBIC_INCHES,
        };
    }, [tiers, frostingStyle]);

    const addTier = () => setTiers([...tiers, { id: Date.now(), shape: 'round', size: '6', layers: '3' }]);
    const removeTier = (id: number) => setTiers(tiers.filter(t => t.id !== id));
    const updateTier = (id: number, field: keyof Tier, value: string) => {
        setTiers(tiers.map(t => (t.id === id ? { ...t, [field]: value } : t)));
    };
    const handleCopy = (value: string, field: string) => {
        navigator.clipboard.writeText(value).then(() => {
            setCopied(field);
            setTimeout(() => setCopied(null), 2000);
        });
    };

    const totalLbs = totalCups / CUPS_PER_POUND;
    const totalGrams = totalLbs * GRAMS_PER_POUND;

    return (
        <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="space-y-8">
                <div className="space-y-4">
                    {tiers.map((tier, index) => (
                        <div key={tier.id} className="bg-white p-5 rounded-xl shadow-sm border border-brand-border text-brand-text-body">
                            <div className="flex justify-between items-center mb-3">
                                <h4 className="font-bold text-brand-text-title text-lg">Tier {index + 1}</h4>
                                {tiers.length > 1 && <button onClick={() => removeTier(tier.id)} className="text-gray-400 hover:text-red-500 transition-colors p-1"><Trash2Icon className="w-5 h-5" /></button>}
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div><label className="block text-xs font-bold text-gray-500 mb-1">Shape</label><select value={tier.shape} onChange={e => updateTier(tier.id, 'shape', e.target.value as CakeShape)} className="w-full px-3 py-2 bg-gray-50 border border-brand-border rounded-lg text-sm"><option value="round">Round</option><option value="square">Square</option></select></div>
                                <div><label className="block text-xs font-bold text-gray-500 mb-1">Size (in)</label><input type="number" value={tier.size} onChange={e => updateTier(tier.id, 'size', e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-brand-border rounded-lg text-sm" /></div>
                                <div><label className="block text-xs font-bold text-gray-500 mb-1">Layers</label><input type="number" value={tier.layers} onChange={e => updateTier(tier.id, 'layers', e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-brand-border rounded-lg text-sm" /></div>
                            </div>
                        </div>
                    ))}
                    <button onClick={addTier} className="mt-4 w-full py-3 border-2 border-dashed border-brand-pink/30 text-brand-pink font-bold rounded-xl hover:bg-brand-pink/5 transition-colors flex items-center justify-center gap-2"><PlusCircleIcon className="w-5 h-5" /> Add Another Tier</button>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                    <h3 className="font-serif font-bold text-xl mb-3 text-brand-text-title">Frosting Style</h3>
                    <select value={frostingStyle} onChange={e => setFrostingStyle(e.target.value as FrostingStyle)} className="w-full px-4 py-3 bg-white border border-brand-border rounded-lg text-brand-text-body"><option value="naked">Naked (Minimal Frosting)</option><option value="standard">Standard Coat (Smooth Finish)</option><option value="generous">Generous Coat & Piping</option><option value="extra">Fully Decorated (Complex Piping)</option></select>
                </div>
            </div>
            <div className="sticky top-6">
                <div className="bg-brand-pink-light p-8 rounded-2xl border border-brand-pink/20 text-center shadow-sm">
                    <PastryBagIcon className="w-12 h-12 mx-auto text-brand-pink mb-4" />
                    <h3 className="font-serif font-bold text-2xl text-brand-text-title mb-2">Total Buttercream Needed</h3>
                    <div className="bg-white p-6 rounded-xl border border-brand-pink/20 shadow-sm my-6">
                        <div className="flex items-center justify-center gap-3">
                            <p className="font-serif font-bold text-7xl text-brand-pink tracking-tighter">{totalCups.toFixed(1)}</p>
                            <div className="text-left"><p className="text-xl font-bold text-brand-text-title">Cups</p><button onClick={() => handleCopy(totalCups.toFixed(1), 'cups')} className="text-xs text-brand-pink hover:underline flex items-center gap-1">{copied === 'cups' ? <CheckCircleIcon className="w-3 h-3" /> : <ClipboardIcon className="w-3 h-3" />}{copied === 'cups' ? 'Copied' : 'Copy'}</button></div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                        <div className="bg-white/60 p-4 rounded-xl border border-brand-pink/10"><p className="font-bold text-brand-text-title text-2xl">{totalLbs.toFixed(2)}</p><p className="font-medium text-brand-text-body/70">Pounds</p></div>
                        <div className="bg-white/60 p-4 rounded-xl border border-brand-pink/10"><p className="font-bold text-brand-text-title text-2xl">{totalGrams.toFixed(0)}</p><p className="font-medium text-brand-text-body/70">Grams</p></div>
                    </div>
                    <div className="text-left text-sm space-y-3 bg-white/40 p-4 rounded-xl border border-brand-pink/10">
                        <div className="flex justify-between items-center border-b border-brand-pink/10 pb-2"><span className="text-brand-text-body/80">For Filling:</span><span className="font-bold text-brand-text-title">~{fillingCups.toFixed(1)} cups</span></div>
                        <div className="flex justify-between items-center"><span className="text-brand-text-body/80">For Coating & Decor:</span><span className="font-bold text-brand-text-title">~{coatingCups.toFixed(1)} cups</span></div>
                    </div>
                </div>
            </div>
        </div>
    );
};
